// @flow

import { useCallback, useRef } from 'react'
import * as Sentry from '@sentry/react'

export const RELOAD_TIMEOUT_MS = 2000

const INDEX_HTML = 'index.html'

// The packaged TV app runs from file:///index.html and the router appends its path to it
// (file:///index.html/settings/support), so a plain reload opens a missing file.
// Same approach as reloadApp in smart-tv/brands/*/bootloader/scripts.js.
export const restartApp = () => {
  const { href } = window.location
  const index = href.indexOf(INDEX_HTML)

  if (index === -1) {
    window.location.reload()
  } else {
    window.location.href = href.slice(0, index + INDEX_HTML.length)
  }
}

// Gives Sentry a chance to send the crash event before the page goes away.
// The reload never waits longer than RELOAD_TIMEOUT_MS, whatever flush does.
export const useReload = (): (() => void) => {
  const startedRef = useRef(false)

  return useCallback(() => {
    if (startedRef.current) return

    startedRef.current = true

    let reloaded = false
    let timer = null

    const reload = () => {
      if (reloaded) return

      reloaded = true
      clearTimeout(timer)
      restartApp()
    }

    timer = setTimeout(reload, RELOAD_TIMEOUT_MS)

    try {
      Promise.resolve(Sentry.flush(RELOAD_TIMEOUT_MS)).then(reload).catch(reload)
    } catch (e) {
      reload()
    }
  }, [])
}
